import React, { ChangeEvent, useContext, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { faFilePdf, faPaperPlane, faUpload } from '@fortawesome/free-solid-svg-icons'
import { faTrashCan } from '@fortawesome/free-solid-svg-icons/faTrashCan'
import Button, { ButtonSize, ButtonTheme } from 'components/Form/Button'
import ModalAction from 'components/Modal/ModalAction'
import { AppContext } from 'contexts/AppContext'
import { DispositionCorrection } from 'types/disposition'
import service from '../service'

interface CorrectionInvoiceActionsProps {
  correction: DispositionCorrection
  onChanged: () => void
  /** The client only downloads the invoice - attaching, sending and removing stay with AMS users. */
  readOnly?: boolean
}

/**
 * A corrective invoice delivered by the mailbox job has already been emailed, so it only gets a
 * download. When the job did not attach one, the user uploads the pdf and sends it once in a
 * separate step - the same flow as for the disposition invoice.
 */
const CorrectionInvoiceActions: React.FC<CorrectionInvoiceActionsProps> = ({
  correction,
  onChanged,
  readOnly = false,
}) => {
  const { t } = useTranslation()
  const { addNotification } = useContext(AppContext)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState<boolean>(false)

  const attachInvoice = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0]
    // Allows picking the same file again after a failed upload
    event.target.value = ''

    if (!file) {
      return
    }

    setLoading(true)
    void service
      .attachCorrectionInvoice(correction.id, file, addNotification, t)
      .then(success => success && onChanged())
      .finally(() => setLoading(false))
  }

  const sendInvoice = (): void => {
    setLoading(true)
    void service
      .sendCorrectionInvoice(correction.id, addNotification, t)
      .then(success => success && onChanged())
      .finally(() => setLoading(false))
  }

  const removeInvoice = (): Promise<unknown> =>
    service
      .removeCorrectionInvoice(correction.id, addNotification, t)
      .then(success => success && onChanged())

  if (!readOnly && service.canAttachCorrectionInvoice(correction)) {
    return (
      <>
        <Button
          dataCy='correction-invoice-attach'
          size={ButtonSize.SMALL}
          theme={ButtonTheme.BLUE}
          icon={faUpload}
          tooltip={t('agencies.dispositions.correctionInvoices.attach')}
          disabled={loading}
          onClick={() => fileInputRef.current?.click()}
        />
        <input
          ref={fileInputRef}
          type='file'
          accept='application/pdf'
          hidden
          onChange={attachInvoice}
        />
      </>
    )
  }

  if (!correction.invoiceFileUrl) {
    return null
  }

  return (
    <>
      <Button
        dataCy='correction-invoice-download'
        size={ButtonSize.SMALL}
        theme={ButtonTheme.NAVY_BLUE_OUTLINE}
        icon={faFilePdf}
        tooltip={t('agencies.dispositions.downloadCorrectionInvoice')}
        onClick={() =>
          service.downloadFile(
            correction.invoiceFileUrl,
            service.correctionInvoiceFileName(correction)
          )
        }
      />
      {!readOnly && service.canSendCorrectionInvoice(correction) && (
        <Button
          dataCy='correction-invoice-send'
          size={ButtonSize.SMALL}
          theme={ButtonTheme.NAVY_BLUE_OUTLINE}
          icon={faPaperPlane}
          tooltip={t('agencies.dispositions.correctionInvoices.send')}
          disabled={loading}
          onClick={sendInvoice}
        />
      )}
      {!readOnly && service.canRemoveCorrectionInvoice(correction) && (
        <>
          <Button
            dataCy='correction-invoice-remove'
            size={ButtonSize.SMALL}
            theme={ButtonTheme.RED}
            icon={faTrashCan}
            tooltip={t('agencies.dispositions.correctionInvoices.remove')}
            disabled={loading}
            onClick={() => setIsRemoveModalOpen(true)}
          />
          <ModalAction
            title={t('agencies.dispositions.correctionInvoices.remove')}
            itemText={t('agencies.dispositions.correctionInvoices.removeConfirm')}
            actionText={t('common.remove')}
            isOpen={isRemoveModalOpen}
            setIsOpen={setIsRemoveModalOpen}
            isLoading={loading}
            setIsLoading={setLoading}
            onAction={removeInvoice}
          />
        </>
      )}
    </>
  )
}

export default CorrectionInvoiceActions
